
import React from 'react';
import { format, isSameDay } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { X } from 'lucide-react';
import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { cn } from "@/lib/utils";
import type { DateRange } from "react-day-picker";

interface DateRangePickerProps {
  value: { start: Date; end: Date } | null;
  onChange: (range: { start: Date; end: Date }) => void;
  className?: string;
  onClear?: () => void;
  shortcuts?: Array<{
    label: string;
    getValue: () => { start: Date; end: Date } | { from: Date; to: Date };
  }>;
}

export const DateRangePicker = ({ value, onChange, className, onClear, shortcuts }: DateRangePickerProps) => {
  const [isOpen, setIsOpen] = React.useState(false);
  const [tempSelection, setTempSelection] = React.useState<DateRange | undefined>();

  // Reset tempSelection whenever the picker opens
  React.useEffect(() => {
    if (isOpen) {
      setTempSelection(undefined);
    }
  }, [isOpen]);

  const formatDateRange = () => {
    if (!value) return 'Selecionar datas';
    
    const startFormatted = format(value.start, "dd/MM/yyyy", { locale: ptBR });
    const endFormatted = format(value.end, "dd/MM/yyyy", { locale: ptBR });
    return `${startFormatted} - ${endFormatted}`;
  };

  const handleRangeSelect = (range: DateRange | undefined) => {

    if (!range?.from) {
      setTempSelection(undefined);
      return;
    }

    // If tempSelection is undefined and we receive a range with both from and to,
    // this is the first click (react-day-picker sends the existing range with the clicked day)
    if (tempSelection === undefined && range.from && range.to && !isSameDay(range.from, range.to)) {
      
      // Determine which date was actually clicked by comparing with current values
      // Only if value is not null
      const clickedDate = value && (isSameDay(range.from, value.start) ? range.to : range.from);
      
      setTempSelection({ from: clickedDate || range.from });
      return;
    }

    // Quando inicia nova seleção (apenas início ou início igual ao fim)
    if (!range.to || isSameDay(range.from, range.to)) {
      setTempSelection({ from: range.from });
      return;
    }

    // Quando um intervalo válido é selecionado (segundo clique)
    if (range.from && range.to && !isSameDay(range.from, range.to)) {
      const startDate = range.from < range.to ? range.from : range.to;
      const endDate = range.from < range.to ? range.to : range.from;

      onChange({
        start: startDate,
        end: endDate
      });

      setTempSelection(undefined);
      setTimeout(() => setIsOpen(false), 100);
    }
  };

  // Usar seleção temporária se disponível, senão usar os valores atuais (ou undefined se value for null)
  const currentRange: DateRange = tempSelection || (value ? {
    from: value.start,
    to: value.end
  } : undefined);

  const handleShortcutClick = (shortcut: typeof shortcuts[0]) => {
    const range = shortcut.getValue();
    // Normalize the range format (from/to -> start/end)
    const normalizedRange = 'from' in range 
      ? { start: range.from, end: range.to }
      : range;
    
    onChange(normalizedRange);
    setTempSelection(undefined);
    setTimeout(() => setIsOpen(false), 100);
  };

  const innerWidthClass = onClear ? "flex-1" : "w-full";

  const picker = (
    <Popover open={isOpen} onOpenChange={setIsOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          className={cn(
            `${innerWidthClass} h-10 justify-start text-left font-normal border border-gray-300 rounded-lg px-3 py-2 gap-0 text-sm focus:ring-2 focus:ring-offset-1 focus:ring-ring`,
            className
          )}
        >
          {formatDateRange()}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-0" align="start">
        {shortcuts && shortcuts.length > 0 && (
          <div className="p-3 border-b bg-background">
            <div className="flex flex-wrap gap-2">
              {shortcuts.map((shortcut) => (
                <Button
                  key={shortcut.label}
                  variant="outline"
                  size="sm"
                  onClick={() => handleShortcutClick(shortcut)}
                  className="text-xs"
                >
                  {shortcut.label}
                </Button>
              ))}
            </div>
          </div>
        )}
        <Calendar
          mode="range"
          selected={currentRange}
          onSelect={handleRangeSelect}
          numberOfMonths={2}
          initialFocus
          className="pointer-events-auto"
          locale={ptBR}
        />
      </PopoverContent>
    </Popover>
  );

  if (!onClear) return picker;

  return (
    <div className="flex items-center gap-1">
      {picker}
      {value && (
        <Button variant="ghost" size="icon" className="h-10 w-10 shrink-0" onClick={onClear}>
          <X className="h-4 w-4" />
        </Button>
      )}
    </div>
  );
};
